import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import persistanceStore from "stores/persistanceStore";

import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import {
  AdminListRequestOfBaseFilter,
  IListResponseOfCertificateTheme,
  ListResponseOfCertificateTheme,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";

interface IListResponseOfCertificateThemeWithId
  extends IListResponseOfCertificateTheme {
  id: string;
}

const CertificateThemeList = () => {
  const { formatMessage: t } = useIntl();
  const {
    certificateThemeDetailsUrl,
    certificateThemeCreateUrl,
    certificateService,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.certificateThemeSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  useListTitle("certificateThemes");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfCertificateTheme>(
    [
      "certificateThemes",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
    ],
    () =>
      certificateService.getCertificateThemes(
        AdminListRequestOfBaseFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      type: "text",
      label: t({ id: "certificateThemeNameLabel" }),
    },
    {
      accessor: "description",
      align: "left",
      type: "text",
      label: t({ id: "certificateThemeDescriptionLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      type: "text",
      label: t({ id: "organization" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(certificateThemeDetailsUrl(id));
  };

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const certificateThemes = data?.data as IListResponseOfCertificateThemeWithId[];

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link
          to={certificateThemeCreateUrl()}
          style={{ textDecoration: "none" }}
        >
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.certificateThemeSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.certificateThemeSearchTerm = e.target.value;
            });
          }}
          data-cy="searchText"
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: certificateThemes ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "certificateThemes" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              certificateThemeDetailsUrl(id)
            ),
          ],
        }}
      />
    </Container>
  );
};

export default CertificateThemeList;
